// /apps/patient/src/pages/records/OrdersTab.tsx

import { useState, useEffect } from 'react';
import { patientApiService } from '../../services/api';
import {
  AlertCircle, ClipboardList, TestTube2, Calendar, Clock,
  ChevronDown, ChevronUp, FileText, Download
} from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '../../lib/utils';

interface Order {
  id: number;
  created_at: number;
  status: string;
  _type: 'provider_order' | 'lab_order';
  // Provider order fields
  description?: string;
  order_type?: string;
  result_values?: Record<string, any>;
  result_notes?: string;
  result_document_ids?: number[];
  result_received_at?: number;
  result_reviewed_at?: number;
  patient_instructions?: string;
  // Lab order fields
  tests?: Array<{ test_name?: string; test_code?: string }>;
  test_code?: string;
  lab_name?: string;
  results_available?: boolean;
  priority?: string;
}

export function OrdersTab() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await patientApiService.getMyOrders();
      setOrders(Array.isArray(response) ? response : []);
    } catch (err: any) {
      console.error('Error fetching orders:', err);
      setError(err.message || 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'in_progress':
      case 'processing':
      case 'results_received':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'pending':
      case 'ordered':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'cancelled':
        return 'text-gray-600 bg-gray-50 border-gray-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const formatStatus = (status: string) => {
    return status
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const formatResultKey = (key: string) => {
    return key
      .replace(/_/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  };

  const getOrderName = (order: Order) => {
    if (order._type === 'lab_order') {
      if (order.tests && Array.isArray(order.tests) && order.tests.length > 0) {
        const testNames = order.tests
          .map(t => t.test_name)
          .filter(Boolean);
        if (testNames.length > 0) return testNames.join(', ');
      }
      return 'Lab Order';
    }
    return order.description || order.order_type || 'Provider Order';
  };

  const hasResults = (order: Order) => {
    if (order.result_values && Object.keys(order.result_values).length > 0) return true;
    if (order.result_notes) return true;
    if (order.result_document_ids && order.result_document_ids.length > 0) return true;
    if (order.patient_instructions) return true;
    return false;
  };

  const toggleExpanded = (orderId: string) => {
    setExpandedOrderId(prev => prev === orderId ? null : orderId);
  };

  const handleDownloadDocument = async (docId: number) => {
    // Documents linked by ID — would need a fetch-by-id endpoint to get the file object.
    // For now, this is a placeholder; the provider uploads these and they appear in DocumentsTab.
  };

  if (loading) {
    return (
      <div className="mt-6">
        <div className="animate-pulse space-y-4">
          <div className="h-24 bg-gray-200 rounded-lg"></div>
          <div className="h-24 bg-gray-200 rounded-lg"></div>
          <div className="h-24 bg-gray-200 rounded-lg"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-3" />
        <p className="text-red-700">{error}</p>
        <button
          onClick={fetchOrders}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="mt-8 text-center py-12 bg-white rounded-lg">
        <ClipboardList className="h-12 w-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500">No orders found</p>
        <p className="text-sm text-gray-400 mt-1">Your orders will appear here</p>
      </div>
    );
  }

  return (
    <div className="mt-6 space-y-4">
      {orders.map((order) => {
        const orderKey = `${order._type}-${order.id}`;
        const isExpanded = expandedOrderId === orderKey;
        const orderHasResults = hasResults(order);

        return (
          <div
            key={orderKey}
            className="bg-white rounded-lg shadow-sm border border-gray-200"
          >
            <div
              className={cn("p-4", orderHasResults && "cursor-pointer")}
              onClick={() => orderHasResults && toggleExpanded(orderKey)}
            >
              <div className="flex items-start gap-4">
                <div className={cn(
                  "p-2 rounded-lg",
                  order._type === 'lab_order' ? "bg-blue-50" : "bg-purple-50"
                )}>
                  {order._type === 'lab_order' ? (
                    <TestTube2 className="h-5 w-5 text-blue-500" />
                  ) : (
                    <ClipboardList className="h-5 w-5 text-purple-500" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-1 flex-wrap">
                    <h3 className="font-medium text-gray-900 truncate">
                      {getOrderName(order)}
                    </h3>
                    <span className={cn(
                      "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border",
                      order._type === 'lab_order'
                        ? "text-blue-700 bg-blue-50 border-blue-200"
                        : "text-purple-700 bg-purple-50 border-purple-200"
                    )}>
                      {order._type === 'lab_order' ? 'Lab Order' : 'Provider Order'}
                    </span>
                  </div>

                  <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mt-2">
                    {order.created_at && (
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {format(new Date(order.created_at), 'MMM d, yyyy')}
                      </span>
                    )}

                    {order._type === 'lab_order' && order.lab_name && (
                      <span className="text-gray-500">{order.lab_name}</span>
                    )}

                    {order._type === 'lab_order' && order.test_code && (
                      <span className="text-gray-400 text-xs">{order.test_code}</span>
                    )}

                    {order.priority && order.priority !== 'routine' && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium text-orange-700 bg-orange-50 border border-orange-200">
                        {formatStatus(order.priority)}
                      </span>
                    )}

                    <span className={cn(
                      "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border",
                      getStatusColor(order.status)
                    )}>
                      {formatStatus(order.status)}
                    </span>
                  </div>

                  {order._type === 'lab_order' && order.results_available && (
                    <div className="mt-2">
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700">
                        <Clock className="h-3 w-3" />
                        Results Available
                      </span>
                    </div>
                  )}
                </div>

                {orderHasResults && (
                  <div className="flex-shrink-0 text-gray-400">
                    {isExpanded ? (
                      <ChevronUp className="h-5 w-5" />
                    ) : (
                      <ChevronDown className="h-5 w-5" />
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Expanded Results Section */}
            {isExpanded && orderHasResults && (
              <div className="border-t border-gray-200 px-4 py-4 space-y-4">
                {/* Structured Result Values */}
                {order.result_values && Object.keys(order.result_values).length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Results</h4>
                    <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                      {Object.entries(order.result_values).map(([key, value]) => (
                        <div key={key} className="flex justify-between items-baseline text-sm">
                          <span className="text-gray-600">{formatResultKey(key)}</span>
                          <span className="font-medium text-gray-900">{String(value)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Result Notes */}
                {order.result_notes && (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Notes</h4>
                    <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3">
                      {order.result_notes}
                    </p>
                  </div>
                )}

                {/* Patient Instructions */}
                {order.patient_instructions && (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Instructions</h4>
                    <p className="text-sm text-gray-700 bg-blue-50 rounded-lg p-3">
                      {order.patient_instructions}
                    </p>
                  </div>
                )}

                {/* Linked Documents */}
                {order.result_document_ids && order.result_document_ids.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                      Attached Documents
                    </h4>
                    <div className="space-y-1">
                      {order.result_document_ids.map((docId) => (
                        <div
                          key={docId}
                          className="flex items-center gap-2 text-sm bg-gray-50 rounded-lg px-3 py-2"
                        >
                          <FileText className="h-4 w-4 text-gray-400" />
                          <span className="flex-1 text-gray-700">Document #{docId}</span>
                        </div>
                      ))}
                      <p className="text-xs text-gray-400 mt-1">
                        These documents are available in the Documents tab
                      </p>
                    </div>
                  </div>
                )}

                {/* Reviewed timestamp */}
                {order.result_reviewed_at && (
                  <p className="text-xs text-gray-400">
                    Reviewed {format(new Date(order.result_reviewed_at * 1000), 'MMM d, yyyy')}
                  </p>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
